import React, {
  ReactNode,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  Button,
} from '@populateui/ui';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

type Part = 'root' | 'content' | 'icon' | 'title' | 'message' | 'actions';

type Props = {
  title?: string;
  message?: ReactNode;
  isRetrying?: boolean;
  onRetry?: () => void;
  onGoBack?: () => void;
  classes?: Partial<Record<Part, string>>;
};

const defaultTitle = 'Unable to load';
const defaultMessage = 'Something went wrong. Please try again.';

export default function LoadError({
  title = defaultTitle,
  message = defaultMessage,
  isRetrying = false,
  onRetry,
  onGoBack,
  classes,
}: Props) {
  return (
    <div className={clsx(
      'flex flex-1 items-center justify-center h-full bg-white rounded-xl',
      classes?.root,
    )}
    >
      <div className={clsx(
        'flex flex-col items-center gap-4 max-w-[500px] px-6 text-center',
        classes?.content,
      )}
      >
        <div className={clsx(
          'flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 text-gray-500',
          classes?.icon,
        )}
        >
          <ErrorOutlineOutlinedIcon className="!w-6 !h-6" />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className={clsx('text-base font-semibold text-gray-900', classes?.title)}>
            {title}
          </h2>
          <p className={clsx('text-sm text-gray-500', classes?.message)}>
            {message ?? defaultMessage}
          </p>
        </div>
        {(!isNil(onGoBack) || !isNil(onRetry)) && (
          <div className={clsx('flex items-center gap-2 pt-1', classes?.actions)}>
            {!isNil(onGoBack) && (
              <Button
                variant="outlined"
                onClick={onGoBack}
              >
                Go back
              </Button>
            )}
            {!isNil(onRetry) && (
              <Button
                variant="contained"
                disabled={isRetrying}
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
